import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mountain, ShieldCheck, Loader2, CheckCircle2, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DISCLAIMER } from "@/lib/constants";

export default function CompleteRegistration() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const sessionId = new URLSearchParams(window.location.search).get("session_id");

    useEffect(() => {
        if (!sessionId) { setError("Sessão de pagamento não encontrada."); setLoading(false); return; }
        let attempts = 0;
        let timer;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/guest-session/${sessionId}`);
                setInfo(data);
                if (data.paid || attempts >= 8) { setLoading(false); return; }
            } catch (e) {
                setError("Não foi possível carregar os dados da compra.");
                setLoading(false);
                return;
            }
            attempts += 1;
            timer = setTimeout(poll, 2500);
        };
        poll();
        return () => timer && clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error("A senha deve ter no mínimo 6 caracteres."); return; }
        if (password !== confirm) { toast.error("As senhas não coincidem."); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/complete-registration", { session_id: sessionId, password });
            setUser(data);
            toast.success("Cadastro concluído! Bem-vindo(a) ao Geomine Manager.");
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Não foi possível concluir o cadastro.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 topo-texture flex flex-col">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center">
                    <Link to="/" className="flex items-center gap-2 font-heading font-black text-lg text-petroleum" data-testid="complete-logo">
                        <Mountain className="w-6 h-6 text-mineral" /> GEOMINE MANAGER
                    </Link>
                </div>
            </header>

            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-8" data-testid="complete-registration-card">
                    {loading ? (
                        <div className="text-center py-8" data-testid="complete-loading">
                            <Loader2 className="w-8 h-8 text-mineral animate-spin mx-auto" />
                            <p className="mt-4 text-slate-600">Confirmando seu pagamento...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-6" data-testid="complete-error">
                            <p className="text-slate-800 font-semibold">{error}</p>
                            <button onClick={() => navigate("/")} className="mt-6 text-mineral font-semibold hover:underline" data-testid="complete-home-button">Voltar ao início</button>
                        </div>
                    ) : info?.already_registered ? (
                        <div className="text-center py-6" data-testid="complete-already-registered">
                            <CheckCircle2 className="w-10 h-10 text-mineral mx-auto" />
                            <h1 className="mt-4 font-heading text-xl font-black text-slate-900">Conta já existente</h1>
                            <p className="mt-2 text-slate-600 text-sm">Já existe uma conta com o e-mail <strong>{info.email}</strong>. Faça login para acessar sua assinatura.</p>
                            <button onClick={() => navigate("/login")} className="mt-6 w-full bg-petroleum hover:bg-petroleumHover text-white rounded-sm py-3 font-bold" data-testid="complete-login-button">Ir para o login</button>
                        </div>
                    ) : !info?.paid ? (
                        <div className="text-center py-6" data-testid="complete-pending">
                            <Loader2 className="w-8 h-8 text-mineral animate-spin mx-auto" />
                            <p className="mt-4 text-slate-800 font-semibold">Estamos confirmando seu pagamento.</p>
                            <p className="mt-2 text-slate-600 text-sm">Isso pode levar alguns segundos. Você também receberá um e-mail com o link para concluir o cadastro.</p>
                            <button onClick={() => window.location.reload()} className="mt-6 text-mineral font-semibold hover:underline" data-testid="complete-refresh-button">Atualizar</button>
                        </div>
                    ) : (
                        <>
                            <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-mineral/15 text-mineralHover border border-mineral/30">
                                <CheckCircle2 className="w-4 h-4" /> Pagamento confirmado
                            </div>
                            <h1 className="mt-4 font-heading text-2xl font-black text-slate-900">Conclua seu cadastro</h1>
                            <p className="mt-1 text-slate-600 text-sm">Defina uma senha para acessar a plataforma.</p>

                            <div className="mt-5 space-y-2 text-sm bg-slate-50 rounded-lg p-4 border border-slate-200">
                                <div className="flex justify-between"><span className="text-slate-500">Nome</span><span className="font-semibold text-slate-800" data-testid="complete-name">{info.name || "—"}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">CPF</span><span className="font-semibold text-slate-800" data-testid="complete-cpf">{info.cpf || "—"}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">E-mail</span><span className="font-semibold text-slate-800" data-testid="complete-email">{info.email}</span></div>
                            </div>

                            <form onSubmit={submit} className="mt-6 space-y-4">
                                <div>
                                    <label className="text-sm font-semibold text-slate-700">Senha</label>
                                    <div className="mt-1 relative">
                                        <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2.5 rounded-sm border border-slate-300 focus:border-mineral focus:ring-1 focus:ring-mineral outline-none"
                                            placeholder="Mínimo 6 caracteres" data-testid="complete-password-input" required />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-slate-700">Confirmar senha</label>
                                    <div className="mt-1 relative">
                                        <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2.5 rounded-sm border border-slate-300 focus:border-mineral focus:ring-1 focus:ring-mineral outline-none"
                                            placeholder="Repita a senha" data-testid="complete-confirm-input" required />
                                    </div>
                                </div>
                                <button type="submit" disabled={submitting} data-testid="complete-submit-button"
                                    className="w-full bg-mineral hover:bg-mineralHover text-white rounded-sm py-3 font-bold shadow-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Concluindo...</> : "Concluir cadastro e acessar"}
                                </button>
                            </form>
                        </>
                    )}

                    <div className="mt-6 flex items-start gap-2 text-xs text-slate-400">
                        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" /> {DISCLAIMER}
                    </div>
                </div>
            </div>
        </div>
    );
}
